// @ts-nocheck
import { useState, useEffect, useRef, useMemo } from 'react'
import { renderTemplate, TEMPLATE_NAMES } from '../templates/TemplateRenderer.js'
import PartnerImageUpload from './PartnerImageUpload'
import LogoPicker from './LogoPicker'
import SectionTextEditModal from './SectionTextEditModal'
import PartnershipBulletsEditModal from './PartnershipBulletsEditModal'
import { generateCaseStudyPdf } from '../utils/pdf'
import useEditorLayout from '../hooks/useEditorLayout'
import ZoneEditor from './editor/ZoneEditor'
import { ZONE_DEFINITIONS, SECTION_SUB_ELEMENTS } from '../types/editor'
import TitleDescriptionBlocksModal from './TitleDescriptionBlocksModal'
import {
  applyPartnershipBulletsDescriptionsSave,
  applyPreviewModalSave,
  findPreviewRowByTargetId,
  getPreviewModalInitialText,
  previewRowCanEdit,
  previewRowExistsInData,
  isBlockEditKind,
  getBlocksForEditKind,
  applyBlocksSave,
} from '../utils/previewSectionEdit.js'

// Import template images base URL function
const getTemplateImagesBaseUrl = () => {
  const bucketUrl = (typeof import.meta !== 'undefined' && import.meta.env) 
    ? import.meta.env.VITE_TEMPLATE_IMAGES_BASE_URL 
    : undefined;
  
  if (bucketUrl) {
    return bucketUrl;
  }
  
  // Fallback to hardcoded dev bucket URL (regional endpoint) if env var not available
  return 'https://case-study-prod-template-images-1e62f03a.s3.us-east-2.amazonaws.com';
};

const PreviewPage = () => {
  const previewHostRef = useRef(null)
  const [caseStudy, setCaseStudy] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_NAMES.PAGE_1)
  const [renderedHtml, setRenderedHtml] = useState('')
  // Partner image is held as a transient blob URL (not persisted to localStorage)
  const [partnerImageState, setPartnerImageState] = useState(null)
  const [partnerImageSize, setPartnerImageSize] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_partnerImageSize'); return v ? parseInt(v,10) : 100 } catch(e){return 100}
  })
  const [partnerImageOffsetX, setPartnerImageOffsetX] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_partnerImageOffsetX'); return v ? parseInt(v,10) : 0 } catch(e){return 0}
  })
  const [partnerImageOffsetY, setPartnerImageOffsetY] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_partnerImageOffsetY'); return v ? parseInt(v,10) : 0 } catch(e){return 0}
  })

  const [logoLeft, setLogoLeft] = useState(() => localStorage.getItem('htmlPreview_logo_left'))
  const [logoCenter, setLogoCenter] = useState(() => localStorage.getItem('htmlPreview_logo_center'))
  const [logoRight, setLogoRight] = useState(() => localStorage.getItem('htmlPreview_logo_right'))
  const [graphicsLogo1, setGraphicsLogo1] = useState(() => localStorage.getItem('htmlPreview_graphics_logo_1'))
  const [graphicsLogo2, setGraphicsLogo2] = useState(() => localStorage.getItem('htmlPreview_graphics_logo_2'))
  const [graphicsLogo1Size, setGraphicsLogo1Size] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_graphics_logo_1_size'); return v ? parseInt(v,10) : 100 } catch(e){return 100}
  })
  const [graphicsLogo2Size, setGraphicsLogo2Size] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_graphics_logo_2_size'); return v ? parseInt(v,10) : 100 } catch(e){return 100}
  })
  const [graphicsLogo1OffsetX, setGraphicsLogo1OffsetX] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_graphics_logo_1_offsetX'); return v ? parseInt(v,10) : 0 } catch(e){return 0}
  })
  const [graphicsLogo1OffsetY, setGraphicsLogo1OffsetY] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_graphics_logo_1_offsetY'); return v ? parseInt(v,10) : 0 } catch(e){return 0}
  })
  const [graphicsLogo2OffsetX, setGraphicsLogo2OffsetX] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_graphics_logo_2_offsetX'); return v ? parseInt(v,10) : 0 } catch(e){return 0}
  })
  const [graphicsLogo2OffsetY, setGraphicsLogo2OffsetY] = useState(() => {
    try { const v = localStorage.getItem('htmlPreview_graphics_logo_2_offsetY'); return v ? parseInt(v,10) : 0 } catch(e){return 0}
  })


  // PDF generation state
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [pdfError, setPdfError] = useState('')
  
  const [quickEditSection, setQuickEditSection] = useState(null)

  const { layout, updateZone, updateSectionSize, updateSectionSpacing, resetLayout } = useEditorLayout(caseStudy?.data?.job_id)
  const [sectionEditorExpanded, setSectionEditorExpanded] = useState(true)

  // Page 2 visit tracking
  const [hasVisitedPage2, setHasVisitedPage2] = useState(() => {
    try {
      return sessionStorage.getItem('preview_visitedPage2') === 'true'
    } catch (e) {
      return false
    }
  })
  
  // UI state for collapsible sections
  const [isObjectiveLogosExpanded, setIsObjectiveLogosExpanded] = useState(true)

  const [textSizingConfig, setTextSizingConfig] = useState(() => {
    // Try to load from localStorage first
    try {
      const saved = localStorage.getItem('htmlPreview_textSizingConfig')
      if (saved) {
        const parsed = JSON.parse(saved)
        // Convert any preset values to rem values for backward compatibility
        const converted = {}
        Object.keys(parsed).forEach(key => {
          if (parsed[key] === 'small' || parsed[key] === 'medium' || parsed[key] === 'large' || parsed[key] === 'auto-fit') {
            converted[key] = getBaselineRemForSection(key)
          } else {
            converted[key] = parsed[key]
          }
        })
        return converted
      }
    } catch (error) {
      console.error('Failed to load text sizing config:', error)
    }
    
    // Default configuration with baseline REM values
    return {
      title: '2.2rem',
      subtitle: '1.2rem',
      at_glance: '0.75rem',
      key_metrics_title: '0.9rem',
      key_metrics_description: '0.75rem',
      testimonial: '0.75rem',
      challenges: '0.75rem',
      objectives: '0.75rem',
      outcomes: '0.9rem',
      // Page 2 sections  
      partnership_client_overview: '0.8rem',
      partnership_bullets: '0.7rem',
      why_choose_riscpoint: '0.75rem',
      call_to_action: '0.8rem'
    }
  })
  
  // Slider values state (extracted from rem values in textSizingConfig)
  const [sliderValues, setSliderValues] = useState(() => {
    // Initialize from existing textSizingConfig or use defaults
    const defaults = {
      title: 2.2,
      subtitle: 1.2,
      at_glance: 0.75,
      key_metrics_title: 0.9,
      key_metrics_description: 0.75,
      testimonial: 0.75,
      challenges: 0.75,
      objectives: 0.75,
      outcomes: 0.9,
      partnership_client_overview: 0.8,
      partnership_bullets: 0.7,
      why_choose_riscpoint: 0.75,
      call_to_action: 0.8
    }
    
    try {
      const saved = localStorage.getItem('htmlPreview_sliderValues')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.error('Failed to load slider values:', error)
    }
    
    return defaults
  })

  // Get case study data from URL state or localStorage
  useEffect(() => {
    // Try to get data from navigation state first
    const navigationState = window.history.state?.caseStudy
    
    if (navigationState) {
      setCaseStudy(navigationState)
    } else {
      // Fallback: try to get from localStorage
      const storedData = localStorage.getItem('previewCaseStudy')
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData)
          setCaseStudy(parsed)
        } catch (error) {
          console.error('Failed to parse stored case study data:', error)
          // Redirect back to main app if no data available
          window.location.href = '/'
        }
      } else {
        // No data available, redirect back
        window.location.href = '/'
      }
    }
  }, [])

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('htmlPreview_textSizingConfig', JSON.stringify(textSizingConfig))
    } catch (error) {
      console.error('Failed to save text sizing config:', error)
    }
  }, [textSizingConfig])

  useEffect(() => {
    try {
      localStorage.setItem('htmlPreview_sliderValues', JSON.stringify(sliderValues))
    } catch (error) {
      console.error('Failed to save slider values:', error)
    }
  }, [sliderValues])

  // Render template when case study or settings change
  useEffect(() => {
    if (caseStudy?.data) {
      try {
        const result = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout)
        setRenderedHtml(result.html)
      } catch (error) {
        console.error('Template rendering failed:', error)
      }
    }
  }, [caseStudy, selectedTemplate, textSizingConfig, layout])

  useEffect(() => {
    const scaler = previewHostRef.current?.parentElement
    if (!scaler) return
    const update = () => {
      const contentArea = scaler.parentElement
      if (!contentArea) return
      const availW = contentArea.clientWidth
      const availH = contentArea.clientHeight
      const scaleW = availW / 816
      const scaleH = availH / 1056
      const scale = Math.min(scaleW, scaleH, 1)
      const scaledHeight = Math.ceil(1056 * scale)
      const topGap = Math.max(56, Math.floor((availH - scaledHeight) / 2))
      scaler.style.setProperty('--preview-scale', String(scale))
      scaler.style.width = '816px'
      scaler.style.height = scaledHeight + 'px'
      scaler.style.marginTop = topGap + 'px'
      scaler.style.marginBottom = topGap + 'px'
      scaler.style.marginLeft = 'auto'
      scaler.style.marginRight = 'auto'
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(scaler.parentElement)
    return () => ro.disconnect()
  }, [renderedHtml])

  // Inject HTML + logos into preview iframe
  useEffect(() => {
    const el = previewHostRef.current
    if (!el || !renderedHtml) return
    try {
      const doc = el.contentDocument
      if (!doc) return
      doc.open()
      doc.write(renderedHtml)
      doc.close()
    } catch (e) {
      console.error('Failed to write to preview iframe:', e)
      return
    }
    try {
      const doc = el.contentDocument
      const img = partnerImageState
      if (img) {
        const right = doc.querySelector('.partnership-right')
        if (right) {
          right.innerHTML =
            '<img src="' +
            img +
            '" alt="Partner image" class="injected-partner-img" style="width: ' +
            (partnerImageSize || 100) +
            '%; height: auto; transform: translate(calc(-50% + ' + (partnerImageOffsetX || 0) + 'px), calc(-50% + ' + (partnerImageOffsetY || 0) + 'px));" />'
        }
      }

      const curLogoLeft = logoLeft || localStorage.getItem('htmlPreview_logo_left')
      const curLogoCenter = logoCenter || localStorage.getItem('htmlPreview_logo_center')
      const curLogoRight = logoRight || localStorage.getItem('htmlPreview_logo_right')

      if (curLogoLeft) {
        const elLeft = doc.querySelector('.objective-logo-left')
        if (elLeft) elLeft.innerHTML = '<img src="' + curLogoLeft + '" style="max-width:100%; height:auto; object-fit:contain;" />'
      }
      if (curLogoCenter) {
        const elCenter = doc.querySelector('.objective-logo-center')
        if (elCenter) elCenter.innerHTML = '<img src="' + curLogoCenter + '" style="max-width:100%; height:auto; object-fit:contain;" />'
      }
      if (curLogoRight) {
        const elRight = doc.querySelector('.objective-logo-right')
        if (elRight) elRight.innerHTML = '<img src="' + curLogoRight + '" style="max-width:100%; height:auto; object-fit:contain;" />'
      }

      const commonLogoEls = doc.querySelectorAll(
        '.objectives-grid .service-icon-img-small, .objective-icon img, .objective-item .service-icon-img-small'
      )
      if (commonLogoEls && commonLogoEls.length > 0) {
        const fallbacks = [curLogoLeft, curLogoCenter, curLogoRight]
        for (let i = 0; i < Math.min(fallbacks.length, commonLogoEls.length); i++) {
          const src = fallbacks[i]
          if (src) {
            const node = commonLogoEls[i]
            if (node) {
              node.src = src
            }
          }
        }
      }

      if (graphicsLogo1) {
        const graphics1El = doc.querySelector('.graphics-logo-1')
        if (graphics1El) {
          const sizeScale = (graphicsLogo1Size || 100) / 100
          const finalPixelSize = Math.round(60 * sizeScale)
          graphics1El.innerHTML =
            '<img src="' +
            graphicsLogo1 +
            '" class="graphics-injected-img" style="' +
            'width: ' +
            finalPixelSize +
            'px !important;' +
            'height: ' +
            finalPixelSize +
            'px !important;' +
            'object-fit: contain !important;' +
            'position: absolute; top: 50%; left: 50%;' +
            'transform: translate(calc(-50% + ' + (graphicsLogo1OffsetX || 0) + 'px), calc(-50% + ' + (graphicsLogo1OffsetY || 0) + 'px)) !important;' +
            '" />'
        }
      }
      if (graphicsLogo2) {
        const graphics2El = doc.querySelector('.graphics-logo-2')
        if (graphics2El) {
          const sizeScale = (graphicsLogo2Size || 100) / 100
          const finalPixelSize = Math.round(60 * sizeScale)
          graphics2El.innerHTML =
            '<img src="' +
            graphicsLogo2 +
            '" class="graphics-injected-img" style="' +
            'width: ' +
            finalPixelSize +
            'px !important;' +
            'height: ' +
            finalPixelSize +
            'px !important;' +
            'object-fit: contain !important;' +
            'position: absolute; top: 50%; left: 50%;' +
            'transform: translate(calc(-50% + ' + (graphicsLogo2OffsetX || 0) + 'px), calc(-50% + ' + ((graphicsLogo2OffsetY || 0) + 80) + 'px)) !important;' +
            '" />'
        }
      }
    } catch (e) {
      console.error('Injection failed', e)
    }
  }, [
    renderedHtml,
    partnerImageState,
    partnerImageSize,
    partnerImageOffsetX,
    partnerImageOffsetY,
    logoLeft,
    logoCenter,
    logoRight,
    graphicsLogo1,
    graphicsLogo2,
    graphicsLogo1Size,
    graphicsLogo2Size,
    graphicsLogo1OffsetX,
    graphicsLogo1OffsetY,
    graphicsLogo2OffsetX,
    graphicsLogo2OffsetY,
    selectedTemplate,
  ])

  useEffect(() => {
    const el = previewHostRef.current
    if (!el || !caseStudy?.data) return
    const doc = el.contentDocument
    if (!doc) return
    const onClick = (e) => {
      const t = e.target.closest('[data-preview-edit]')
      if (!t) return
      const id = t.getAttribute('data-preview-edit')
      if (!id) return
      const row = findPreviewRowByTargetId(id, selectedTemplate)
      if (!row || !previewRowCanEdit(row, undefined) || !previewRowExistsInData(row, caseStudy.data))
        return
      e.preventDefault()
      setQuickEditSection(row)
    }
    doc.addEventListener('click', onClick)
    return () => doc.removeEventListener('click', onClick)
  }, [renderedHtml, selectedTemplate, caseStudy?.data])

  const handleTemplateSwitch = (templateName) => {
    setSelectedTemplate(templateName)
    
    // Track Page 2 visit
    if (templateName === TEMPLATE_NAMES.PAGE_2 && !hasVisitedPage2) {
      setHasVisitedPage2(true)
      try {
        sessionStorage.setItem('preview_visitedPage2', 'true')
      } catch (e) {
        // Silently handle sessionStorage errors
      }
    }
  }

  const handleBackToEditor = () => {
    // Clear Page 2 visited state since images don't persist
    try {
      sessionStorage.removeItem('preview_visitedPage2')
    } catch (e) {
      // Silently handle sessionStorage errors
    }
    
    // Navigate back to main app and trigger case study restoration
    // Add a URL parameter to trigger the check status logic
    window.location.href = '/?restore=true'
  }


  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true)
    setPdfError('')
    try {
      await generateCaseStudyPdf(caseStudy.data, textSizingConfig, layout)
    } catch (e) {
      setPdfError('Failed to generate PDF. Please try again.')
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  // Handler for slider changes
  const handleSliderChange = (section, value) => {
    const numValue = parseFloat(value)
    setSliderValues(prev => ({
      ...prev,
      [section]: numValue
    }))
    
    setTextSizingConfig(prev => ({
      ...prev,
      [section]: `${value}rem`
    }))
  }

  const partnershipBulletsForEdit = useMemo(() => {
    const pd = caseStudy?.data?.partnership_details
    if (!pd || typeof pd !== "object") return []
    return Array.isArray(pd.partnership_details) ? pd.partnership_details : []
  }, [caseStudy?.data?.partnership_details])

  const getCardsForZone = (zoneId) => {
    const keys = layout.zones[zoneId] || []
    return keys.map((key, index) => ({
      key,
      visible: true,
      order: index,
      size: layout.sectionSizes[key] || 'medium',
    }))
  }

  const MULTIPLIERS_CONST = { small: 0.8, medium: 1.0, large: 1.3 }

  const handleSubSizeChange = (subKey, size) => {
    const parentSection = Object.entries(SECTION_SUB_ELEMENTS).find(
      ([, subs]) => subs.some(s => s.key === subKey)
    )
    if (parentSection) {
      updateSectionSize(parentSection[0], size)
    }

    const subDef = Object.values(SECTION_SUB_ELEMENTS)
      .flat()
      .find(s => s.key === subKey)
    const baseline = subDef?.baseline ?? 0.75
    const rem = (baseline * (MULTIPLIERS_CONST[size] || 1.0)).toFixed(2)
    const remStr = `${rem}rem`

    setTextSizingConfig(prev => ({ ...prev, [subKey]: remStr }))
    setSliderValues(prev => ({ ...prev, [subKey]: parseFloat(rem) }))
  }

  const handleSliderChangeProxy = (subKey, value) => {
    handleSliderChange(subKey, String(value))
  }

  const handleSectionEdit = (sectionKey) => {
    const targetMap = {
      title: 'title', subtitle: 'subtitle', at_a_glance: 'at_a_glance',
      challenges: 'challenges', objectives: 'objectives',
      partnership_details: 'partnership_bullets',
      why_choose_riscpoint: 'partnership_client_overview',
      call_to_action: 'call_to_action',
    }
    const targetId = targetMap[sectionKey]
    if (!targetId || !caseStudy?.data) return
    const row = findPreviewRowByTargetId(targetId, TEMPLATE_NAMES.PAGE_1)
      || findPreviewRowByTargetId(targetId, TEMPLATE_NAMES.PAGE_2)
    if (row && previewRowCanEdit(row, undefined) && previewRowExistsInData(row, caseStudy.data)) {
      setQuickEditSection(row)
    }
  }

  if (!caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading preview...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', overflow: 'hidden' }}>
      {/* Left Sidebar with Controls (scrolls internally) */}
      <div style={{ width: 440, minWidth: 440, maxWidth: 440, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'white', borderRight: '1px solid #e5e7eb' }}>
        {/* Header */}
        <div className="shrink-0 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleBackToEditor}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
            >
              ← Back to Editor
            </button>
            
            <button
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf || !caseStudy?.data || !hasVisitedPage2}
              className={`px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm ${
                hasVisitedPage2 && caseStudy?.data && !isGeneratingPdf
                  ? 'bg-purple-500 text-white hover:bg-purple-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isGeneratingPdf ? 'Generating...' : 'Download PDF (Vector)'}
            </button>
          </div>
          
          {/* Helper text below buttons, centered */}
          {!hasVisitedPage2 && (
            <div className="flex justify-end mb-3">
              <p className="text-sm text-blue-600 font-medium">
                Edit Page 2 First!
              </p>
            </div>
          )}
          
          <h1 className="text-lg font-bold text-gray-800 mb-3">HTML Preview</h1>
          
          {/* PDF Error Display */}
          {pdfError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{pdfError}</p>
              <button 
                onClick={() => setPdfError('')}
                className="text-xs text-red-800 hover:text-red-900 mt-1"
              >
                ✕ Dismiss
              </button>
            </div>
          )}
          
          {/* Template Selection Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handleTemplateSwitch(TEMPLATE_NAMES.PAGE_1)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex-1 ${
                selectedTemplate === TEMPLATE_NAMES.PAGE_1
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Page 1
            </button>
            <button
              onClick={() => handleTemplateSwitch(TEMPLATE_NAMES.PAGE_2)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex-1 ${
                selectedTemplate === TEMPLATE_NAMES.PAGE_2
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Page 2
            </button>
          </div>
        </div>
        
        {/* Text Sizing Controls */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
          {/* Section Layout Editor */}
          <div className="mb-6 border border-blue-200 rounded-lg p-3 bg-blue-50">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold text-gray-800">Section Layout</h2>
              <button
                type="button"
                className="text-xs text-blue-600 hover:text-blue-800"
                onClick={() => setSectionEditorExpanded((o) => !o)}
              >
                {sectionEditorExpanded ? "Hide" : "Show"}
              </button>
            </div>
            {sectionEditorExpanded && (
              <div className="space-y-3">
                {Object.keys(ZONE_DEFINITIONS)
                  .filter((zoneId) => {
                    if (selectedTemplate === TEMPLATE_NAMES.PAGE_1) return zoneId.startsWith('page1')
                    return zoneId === 'page2'
                  })
                  .map((zoneId) => {
                    const allCards = getCardsForZone(zoneId)
                    const fixedKeys = zoneId === 'page1-left' ? ['title', 'subtitle'] : []
                    const fixedItems = fixedKeys
                      .map(k => allCards.find(c => c.key === k))
                      .filter(Boolean)
                    const sortableCards = allCards.filter(c => !fixedKeys.includes(c.key))

                    return (
                      <ZoneEditor
                        key={zoneId}
                        zoneId={zoneId}
                        cards={sortableCards}
                        fixedItems={fixedItems}
                        onCardsChange={updateZone}
                        onSubSizeChange={handleSubSizeChange}
                        onSliderChange={handleSliderChangeProxy}
                        onEdit={handleSectionEdit}
                        sliderValues={sliderValues}
                        sectionSpacing={layout.sectionSpacing || {}}
                        onSpacingChange={updateSectionSpacing}
                      />
                    )
                  })}
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetLayout}
                    className="flex-1 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                  >
                    Reset Layout
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Partner Image Upload (only available on Page 2) */}
          {selectedTemplate === TEMPLATE_NAMES.PAGE_2 && (
            <div className="mb-6">
              {/*<label className="text-sm font-medium text-gray-700 block mb-3">Upload client logo</label>*/}
              
               <PartnerImageUpload onChange={async (src) => {
                // Persist for PDF generator; it will convert blob->data URL
                try { localStorage.setItem('preview_partner_image', src) } catch (e) {}
                setPartnerImageState(src)
                if (caseStudy?.data) {
                  const result = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout)
                  setRenderedHtml(result.html)
                }
              }} />

              {/* Preview and Clear Button */}

             {/* Partner image size slider */}
             <div className="mt-3">
               <label className="text-sm text-gray-700 block">Logo size: {partnerImageSize}%</label>
               <input
                 type="range"
                 min="20"
                 max="200"
                 step="1"
                 value={partnerImageSize}
                 onChange={(e) => {
                   const v = parseInt(e.target.value,10);
                   setPartnerImageSize(v);
                   try { localStorage.setItem('htmlPreview_partnerImageSize', String(v)) } catch(e){}
                   if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) }
                 }}
                 className="w-full slider"
                />
              </div>
              <div className="mt-2">
                <label className="text-sm text-gray-700 block">Position X: {partnerImageOffsetX}px</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={partnerImageOffsetX}
                  onChange={(e) => {
                    const v = parseInt(e.target.value,10);
                    setPartnerImageOffsetX(v);
                    try { localStorage.setItem('htmlPreview_partnerImageOffsetX', String(v)) } catch(e){}
                  }}
                  className="w-full slider"
                />
              </div>
              <div className="mt-2">
                <label className="text-sm text-gray-700 block">Position Y: {partnerImageOffsetY}px</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={partnerImageOffsetY}
                  onChange={(e) => {
                    const v = parseInt(e.target.value,10);
                    setPartnerImageOffsetY(v);
                    try { localStorage.setItem('htmlPreview_partnerImageOffsetY', String(v)) } catch(e){}
                  }}
                  className="w-full slider"
                />
              </div>
             </div>
          )}

          {/* Collapsible Logo picker for objectives (only on Page 1) */}
          {selectedTemplate === TEMPLATE_NAMES.PAGE_1 && (
            <div className="mb-6">
              <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-4">
                <div className="text-sm text-gray-600 font-medium">
                  🎯 Objective Logos
                </div>
                <button
                  onClick={() => setIsObjectiveLogosExpanded(!isObjectiveLogosExpanded)}
                  className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-200 rounded hover:bg-blue-50 transition-colors flex items-center gap-1"
                >
                  {isObjectiveLogosExpanded ? '▼ Collapse' : '▶ Expand'}
                </button>
              </div>
              
              {isObjectiveLogosExpanded && (
                <LogoPicker
                   availableLogos={[
                     // Original 3 service logos
                     { key: 'professional_services', src: `${getTemplateImagesBaseUrl()}/professional_services.png` },
                     { key: 'information_technology', src: `${getTemplateImagesBaseUrl()}/information_technology.png` },
                     { key: 'account_management', src: `${getTemplateImagesBaseUrl()}/account_management.png` },
                     // Numbered logos 1-14
                     { key: 'logo_1', src: `${getTemplateImagesBaseUrl()}/1.png` },
                     { key: 'logo_2', src: `${getTemplateImagesBaseUrl()}/2.png` },
                     { key: 'logo_3', src: `${getTemplateImagesBaseUrl()}/3.png` },
                     { key: 'logo_4', src: `${getTemplateImagesBaseUrl()}/4.png` },
                     { key: 'logo_5', src: `${getTemplateImagesBaseUrl()}/5.png` },
                     { key: 'logo_6', src: `${getTemplateImagesBaseUrl()}/6.png` },
                     { key: 'logo_7', src: `${getTemplateImagesBaseUrl()}/7.png` },
                     { key: 'logo_8', src: `${getTemplateImagesBaseUrl()}/8.png` },
                     { key: 'logo_9', src: `${getTemplateImagesBaseUrl()}/9.png` },
                     { key: 'logo_10', src: `${getTemplateImagesBaseUrl()}/10.png` },
                     { key: 'logo_11', src: `${getTemplateImagesBaseUrl()}/11.png` },
                     { key: 'logo_12', src: `${getTemplateImagesBaseUrl()}/12.png` },
                     { key: 'logo_13', src: `${getTemplateImagesBaseUrl()}/13.png` }
                   ]}
                   selections={{ left: logoLeft, center: logoCenter, right: logoRight, graphics1: graphicsLogo1, graphics2: graphicsLogo2 }}
                   onAssign={(slot, src) => {
                     try { 
                       if (slot === 'graphics1' || slot === 'graphics2') {
                         localStorage.setItem(`htmlPreview_graphics_logo_${slot === 'graphics1' ? '1' : '2'}`, src)
                       } else {
                         localStorage.setItem(`htmlPreview_logo_${slot}`, src) 
                       }
                     } catch(e){}
                     if (slot === 'left') setLogoLeft(src)
                     if (slot === 'center') setLogoCenter(src)
                     if (slot === 'right') setLogoRight(src)
                     if (slot === 'graphics1') setGraphicsLogo1(src)
                     if (slot === 'graphics2') setGraphicsLogo2(src)
                     if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) }
                   }}
                  showGraphicsSlots={selectedTemplate === TEMPLATE_NAMES.PAGE_2}
                  showPositionSlots={selectedTemplate === TEMPLATE_NAMES.PAGE_1}
                  objectivesCount={Array.isArray(caseStudy?.data?.objectives) ? Math.max(1, Math.min(3, caseStudy.data.objectives.length)) : 3}
                />
              )}
            </div>
          )}
          
          <div className="space-y-6">
            {/* Graphics Upload Section (Page 2 only) */}
            {selectedTemplate === TEMPLATE_NAMES.PAGE_2 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <div className="text-sm text-gray-600 font-medium">
                    📸 Graphics Upload (Page 2)
                  </div>
                </div>
                
                {/* Hidden inputs for graphics uploads */}
                <input id="riscpoint1-input" type="file" accept="image/png, image/jpeg, image/webp" className="hidden" onChange={(e)=>{
                  const f = e.target.files && e.target.files[0]; if (!f) return; const url = URL.createObjectURL(f); try { localStorage.setItem('htmlPreview_graphics_logo_1', url) } catch(e){} setGraphicsLogo1(url); if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) } }} />
                <input id="riscpoint2-input" type="file" accept="image/png, image/jpeg, image/webp" className="hidden" onChange={(e)=>{
                  const f = e.target.files && e.target.files[0]; if (!f) return; const url = URL.createObjectURL(f); try { localStorage.setItem('htmlPreview_graphics_logo_2', url) } catch(e){} setGraphicsLogo2(url); if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) } }} />

                {/* Graphics Logo 1 Section */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  {/*<label className="text-sm font-medium text-gray-700 block mb-3">Graphics Logo 1</label>*/}
                  
                  <div className="flex gap-2 items-center mb-3">
                    <button
                      onClick={() => document.getElementById('riscpoint1-input')?.click()}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                    >Upload Image 1</button>
                    <button
                      onClick={() => {
                        // Clear file input to allow upload button to work after preset
                        const fileInput = document.getElementById('riscpoint1-input');
                        if (fileInput) fileInput.value = '';
                        
                        const src = `${getTemplateImagesBaseUrl()}/stateramp_badge.png`;
                        try { localStorage.setItem('htmlPreview_graphics_logo_1', src) } catch(e){}
                        setGraphicsLogo1(src);
                        if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) }
                      }}
                      className="px-3 py-1 bg-gray-100 rounded text-sm"
                    >StateRAMP Badge</button>
                  </div>

                  {/* Graphics Logo 1 size slider */}
                  <div className="mt-3">
                    <label className="text-sm text-gray-700 block">Logo 1 size: {graphicsLogo1Size}%</label>
                    <input
                      type="range"
                      min="20"
                      max="300"
                      step="1"
                      value={graphicsLogo1Size}
                      onChange={(e) => {
                        const v = parseInt(e.target.value,10);
                        setGraphicsLogo1Size(v);
                        try { localStorage.setItem('htmlPreview_graphics_logo_1_size', String(v)) } catch(e){}
                        if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) }
                      }}
                      className="w-full slider"
                    />
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-gray-600 block">X: {graphicsLogo1OffsetX}px</label>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={graphicsLogo1OffsetX}
                      onChange={(e) => {
                        const v = parseInt(e.target.value,10);
                        setGraphicsLogo1OffsetX(v);
                        try { localStorage.setItem('htmlPreview_graphics_logo_1_offsetX', String(v)) } catch(e){}
                      }}
                      className="w-full slider"
                    />
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-gray-600 block">Y: {graphicsLogo1OffsetY}px</label>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={graphicsLogo1OffsetY}
                      onChange={(e) => {
                        const v = parseInt(e.target.value,10);
                        setGraphicsLogo1OffsetY(v);
                        try { localStorage.setItem('htmlPreview_graphics_logo_1_offsetY', String(v)) } catch(e){}
                      }}
                      className="w-full slider"
                    />
                  </div>
                </div>

                {/* Graphics Logo 2 Section */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  {/*<label className="text-sm font-medium text-gray-700 block mb-3">Graphics Logo 2</label>*/}
                  
                  <div className="flex gap-2 items-center mb-3">
                    <button
                      onClick={() => document.getElementById('riscpoint2-input')?.click()}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                    >Upload Image 2</button>
                    <button
                      onClick={() => {
                        // Clear file input to allow upload button to work after preset
                        const fileInput = document.getElementById('riscpoint2-input');
                        if (fileInput) fileInput.value = '';
                        
                        const src = `${getTemplateImagesBaseUrl()}/aws_badge.png`;
                        try { localStorage.setItem('htmlPreview_graphics_logo_2', src) } catch(e){}
                        setGraphicsLogo2(src);
                        if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) }
                      }}
                      className="px-3 py-1 bg-gray-100 rounded text-sm"
                    >AWS Badge</button>
                  </div>

                  {/* Graphics Logo 2 size slider */}
                  <div className="mt-3">
                    <label className="text-sm text-gray-700 block">Logo 2 size: {graphicsLogo2Size}%</label>
                    <input
                      type="range"
                      min="20"
                      max="300"
                      step="1"
                      value={graphicsLogo2Size}
                      onChange={(e) => {
                        const v = parseInt(e.target.value,10);
                        setGraphicsLogo2Size(v);
                        try { localStorage.setItem('htmlPreview_graphics_logo_2_size', String(v)) } catch(e){}
                        if (caseStudy?.data) { const r = renderTemplate(caseStudy.data, selectedTemplate, textSizingConfig, layout); setRenderedHtml(r.html) }
                      }}
                      className="w-full slider"
                    />
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-gray-600 block">X: {graphicsLogo2OffsetX}px</label>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={graphicsLogo2OffsetX}
                      onChange={(e) => {
                        const v = parseInt(e.target.value,10);
                        setGraphicsLogo2OffsetX(v);
                        try { localStorage.setItem('htmlPreview_graphics_logo_2_offsetX', String(v)) } catch(e){}
                      }}
                      className="w-full slider"
                    />
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-gray-600 block">Y: {graphicsLogo2OffsetY}px</label>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={graphicsLogo2OffsetY}
                      onChange={(e) => {
                        const v = parseInt(e.target.value,10);
                        setGraphicsLogo2OffsetY(v);
                        try { localStorage.setItem('htmlPreview_graphics_logo_2_offsetY', String(v)) } catch(e){}
                      }}
                      className="w-full slider"
                    />
                  </div>
                </div>
              </div>
            )}
            
          </div>
        </div>
      </div>

      {/* Main content: scrollable preview area */}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', height: '100%', background: '#f9fafb', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        {renderedHtml ? (
          <div className="preview-scaler">
            <iframe
              ref={previewHostRef}
              className="preview-iframe"
              title="Case Study Preview"
            />
          </div>
        ) : null}
      </div>

      {/* Enhanced styles for responsive and print */}
      <style>{`
        .preview-layout-root {
          height: 100vh !important;
          height: 100dvh !important;
          overflow: hidden !important;
          position: relative !important;
        }

        .preview-sidebar {
          width: 440px;
          min-width: 440px;
          max-width: 440px;
          height: 100%;
          overflow: hidden;
        }

        .preview-sidebar-scroll {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
        }

        .preview-content-area {
          flex: 1;
          min-width: 0;
          overflow-y: auto;
          height: 100%;
        }

        .preview-scaler {
          width: 816px;
          position: relative;
          transform-origin: top center;
          transform: scale(var(--preview-scale, 0.55));
        }
        .preview-iframe {
          width: 816px;
          height: 1056px;
          border: none;
          display: block;
          background: white;
          box-shadow: 0 4px 24px rgba(0,0,0,0.12);
        }

        /* Click-to-edit targets inside preview iframe */
        .preview-html-host [data-preview-edit] {
          cursor: pointer;
          transition: outline-color 0.12s ease, outline-offset 0.12s ease;
        }
        .preview-html-host [data-preview-edit]:hover {
          outline: 2px solid rgba(37, 99, 235, 0.45);
          outline-offset: 3px;
        }

        /* Custom slider styles */
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background: #22559c;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          border: 2px solid white;
        }
        
        .slider::-moz-range-thumb {
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background: #22559c;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          border: 2px solid white;
        }
        
        .slider::-webkit-slider-track {
          height: 8px;
          border-radius: 4px;
          background: #e5e7eb;
        }
        
        .slider::-moz-range-track {
          height: 8px;
          border-radius: 4px;
          background: #e5e7eb;
        }
        
        .slider:focus {
          outline: none;
          ring: 2px solid #22559c;
        }
        
        @media print {
          @page {
            size: 8.5in 11in;
            margin: 0;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:p-0 {
            padding: 0 !important;
            margin: 0 !important;
          }
          
          .print\\:block {
            display: block !important;
          }
          
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          
          /* Hide sidebar in print */
          .preview-sidebar {
            display: none !important;
          }
          
          /* Make main content full width in print */
          .preview-content-area {
            width: 100% !important;
            display: block !important;
          }
          
          /* Force print of all colors and backgrounds */
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ensure background colors and images print */
          div, span, section, article, p, h1, h2, h3, h4, h5, h6 {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Force blue backgrounds in key metrics and objectives */
          [style*="background"], [style*="gradient"], .bg-blue-500, .bg-blue-600, .bg-gradient-to-r {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: inherit !important;
          }
          
          /* Remove all constraints that could cause scaling */
          body, html {
            margin: 0 !important;
            padding: 0 !important;
            width: auto !important;
            height: auto !important;
            min-height: auto !important;
            max-width: none !important;
            max-height: none !important;
            overflow: visible !important;
          }
          
          /* Print-specific graphics logo constraints - generous bounds for scaling */
          .graphics-logos-container {
            width: 100% !important;
            max-width: 4in !important;
            height: 300px !important;
            max-height: 300px !important;
            overflow: hidden !important;
          }
          
          .graphics-logo-slot {
            width: 150px !important;
            height: 150px !important;
            max-width: 150px !important;
            max-height: 150px !important;
            overflow: hidden !important;
            position: relative !important;
          }
          
          /* Page 2 graphics placeholder images (not injected ones) */
          .graphics-logo-slot img:not(.graphics-injected-img) {
            position: absolute !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            max-width: 80px !important;
            max-height: 80px !important;
            object-fit: contain !important;
          }
          
          /* Page 2 Graphics injected images - positioning only, sizing via inline styles */
          .graphics-injected-img {
            position: absolute !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            object-fit: contain !important;
          }
          
          /* Ensure Page 2 graphics containers accommodate staggered positioning in print */
          .graphics-logo-1, .graphics-logo-2 {
            position: relative !important;
          }
          
          /* Let content flow naturally at full size */
          * {
            box-sizing: border-box !important;
            max-width: none !important;
            max-height: none !important;
          }
          
          /* Preserve header title and subtitle width constraints in print */
          .header h1 {
            max-width: 66% !important;
          }
          
          .header .subtitle {
            max-width: 66% !important;
          }
        }
        
        /* Responsive design for smaller screens */
        @media (max-width: 1024px) {
          .preview-sidebar {
            width: 20rem !important;
            min-width: 20rem !important;
            max-width: 20rem !important;
          }
        }
        
        @media (max-width: 768px) {
          .preview-layout-root {
            flex-direction: column !important;
          }
          
          .preview-sidebar {
            width: 100% !important;
            flex: 0 1 min(42vh, 22rem) !important;
            min-height: 0 !important;
            max-height: min(42vh, 22rem) !important;
            border-right: none !important;
            border-bottom: 1px solid #e5e7eb !important;
            overflow: hidden !important;
          }
          
          .preview-content-area {
            flex: 1 1 auto !important;
            min-height: 0 !important;
            height: auto !important;
          }
        }
      `}</style>

      {/* Generating PDF Modal */}
      {isGeneratingPdf && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
              <h3 className="text-lg font-semibold text-gray-800">Generating PDF...</h3>
            </div>
            <p className="text-sm text-gray-600">
              Please wait while we generate your case study PDF. This may take a few moments.
            </p>
          </div>
        </div>
      )}

      {quickEditSection?.editKind === "partnership_bullets" && caseStudy?.data && (
        <PartnershipBulletsEditModal
          key="partnership-bullets-edit"
          open
          onClose={() => setQuickEditSection(null)}
          bullets={partnershipBulletsForEdit}
          onSave={(descriptions) => {
            const newData = applyPartnershipBulletsDescriptionsSave(caseStudy, descriptions)
            setCaseStudy((prev) => {
              if (!prev?.data) return prev
              const next = { ...prev, data: newData }
              try {
                localStorage.setItem("previewCaseStudy", JSON.stringify(next))
              } catch {
                /* ignore quota */
              }
              return next
            })
          }}
        />
      )}

      {quickEditSection && isBlockEditKind(quickEditSection.editKind) && quickEditSection.editKind !== "partnership_bullets" && caseStudy?.data && (
        <TitleDescriptionBlocksModal
          key={quickEditSection.previewTargetId}
          open
          onClose={() => setQuickEditSection(null)}
          sectionLabel={quickEditSection.label}
          blocks={getBlocksForEditKind(quickEditSection.editKind, caseStudy.data)}
          hideTitle={quickEditSection.editKind === "objectives" || quickEditSection.editKind === "challenges"}
          onSave={(blocks) => {
            const newData = applyBlocksSave(quickEditSection.editKind, blocks, caseStudy.data)
            setCaseStudy((prev) => {
              if (!prev?.data) return prev
              const next = { ...prev, data: newData }
              try {
                localStorage.setItem("previewCaseStudy", JSON.stringify(next))
              } catch {
                /* ignore quota */
              }
              return next
            })
          }}
        />
      )}

      {quickEditSection && !isBlockEditKind(quickEditSection.editKind) && caseStudy?.data && (
        <SectionTextEditModal
          key={quickEditSection.previewTargetId}
          open
          onClose={() => setQuickEditSection(null)}
          sectionLabel={quickEditSection.label}
          initialText={getPreviewModalInitialText(quickEditSection, caseStudy.data)}
          onSave={(t) => {
            const newData = applyPreviewModalSave(quickEditSection, t, caseStudy)
            setCaseStudy((prev) => {
              if (!prev?.data) return prev
              const next = { ...prev, data: newData }
              try {
                localStorage.setItem("previewCaseStudy", JSON.stringify(next))
              } catch {
                /* ignore quota */
              }
              return next
            })
          }}
        />
      )}

    </div>
  )
}

export default PreviewPage